import { useId } from 'react';
import type { ComponentProps } from 'react';
import Switch from 'react-switch';
import { IconTrash } from '../icons';
import { useResolvedTokens } from '../../lib/useResolvedToken';

/** Tokens the switch needs resolved to literal colors — react-switch takes real color strings,
 *  not `var(--x)` references. Module-level (not per-render) so `useResolvedTokens`'s dependency
 *  key stays stable across renders. */
const SWITCH_TOKENS = ['--accent', '--bg-elevated', '--text'] as const;

interface TaskToggleRowProps {
  name: string;
  meta: string;
  verified: boolean;
  disabledReason?: string | null;
  checked: boolean;
  pending: boolean;
  onToggle: () => void;
  onDelete?: () => void;
}

/** One catalog or custom task row with an on/off switch, used throughout Task Setup. */
export function TaskToggleRow({
  name,
  meta,
  verified,
  disabledReason,
  checked,
  pending,
  onToggle,
  onDelete,
}: TaskToggleRowProps) {
  const switchId = useId();
  const tokens = useResolvedTokens(SWITCH_TOKENS);
  // `exactOptionalPropertyTypes` forbids passing `offColor={undefined}` etc. explicitly — react-switch's
  // color props are `?: string`, not `?: string | undefined`, so an unresolved token (first paint,
  // before the effect below runs) has to be OMITTED, not set to undefined. Falls back to
  // react-switch's own defaults for that one frame.
  const switchColorProps: Partial<
    Pick<ComponentProps<typeof Switch>, 'onColor' | 'offColor' | 'onHandleColor' | 'offHandleColor'>
  > = {};
  if (tokens['--accent']) switchColorProps.onColor = tokens['--accent'];
  if (tokens['--bg-elevated']) switchColorProps.offColor = tokens['--bg-elevated'];
  if (tokens['--text']) {
    switchColorProps.onHandleColor = tokens['--text'];
    switchColorProps.offHandleColor = tokens['--text'];
  }

  return (
    <li
      className="flex min-h-tap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2"
      title={disabledReason ?? undefined}
    >
      <div className={['min-w-0 flex-1', disabledReason ? 'opacity-50' : ''].join(' ')}>
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-text">{name}</p>
          {!verified && (
            <span
              className="cursor-help text-[0.625rem] font-semibold uppercase tracking-wider text-warning underline decoration-dotted decoration-warning/70 underline-offset-2"
              title="This value comes from research that hasn't been confirmed in-game yet."
            >
              Unverified
            </span>
          )}
        </div>
        <p className="truncate text-xs text-text-faint">{disabledReason ?? meta}</p>
      </div>

      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete custom task ${name}`}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
        >
          <IconTrash />
        </button>
      )}

      {/* REV18 (docs/specs/REV18-switch-classnames-toast-theme.md): react-switch replaces the old
          hand-rolled `role="switch"` pill, whose knob jumped clean off the right edge of the track
          when enabled — root cause + verification recorded in that spec's Deviations. react-switch
          nests a REAL `<input type="checkbox" role="switch">` (visually hidden, 1x1px) inside a
          decorative track/handle pair, so unlike the old `<button>`, a `<label>` wrapper here DOES
          get native label-click-forwarding — the whole 44x44 box is a working tap target without
          inflating the switch's own compact visual size. Colors come from `useResolvedTokens`
          (lib/useResolvedToken.ts) since react-switch takes literal color strings, not `var(--x)`
          references. */}
      <label
        htmlFor={switchId}
        className={[
          'flex min-h-tap min-w-tap shrink-0 items-center justify-center',
          pending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        ].join(' ')}
      >
        <Switch
          id={switchId}
          checked={checked}
          onChange={onToggle}
          disabled={pending}
          aria-label={`Enable ${name}`}
          height={28}
          width={48}
          handleDiameter={24}
          checkedIcon={false}
          uncheckedIcon={false}
          {...switchColorProps}
        />
      </label>
    </li>
  );
}
